/**
 * Pure helpers for shaping the page tree.
 *
 * The server stores pages as a flat list scoped to a workspace. The sidebar
 * wants a recursive tree with depth-aware indentation and accordion state.
 * These helpers never touch the network — they take the flat array the API
 * already returns and produce the view model the components render.
 */

import type { Page } from '@friday/shared/realtime'

/** A page enriched with its position in the rendered tree. */
export interface PageNode {
  page: Page
  children: PageNode[]
}

/**
 * Build a nested tree from a flat list of pages. The list must be the full
 * workspace's pages (not a slice) — the tree walker needs every row to find
 * a node's parent. Order inside each level is by `position` ascending.
 */
export function buildTree(pages: Page[]): PageNode[] {
  const byId = new Map<string, PageNode>()
  for (const p of pages) byId.set(p.id, { page: p, children: [] })
  const roots: PageNode[] = []
  for (const p of pages) {
    const node = byId.get(p.id)!
    if (p.parentPageId) {
      const parent = byId.get(p.parentPageId)
      if (parent) parent.children.push(node)
      else roots.push(node) // orphan — treat as root
    } else {
      roots.push(node)
    }
  }
  const sortRec = (ns: PageNode[]) => {
    ns.sort((a, b) => a.page.position - b.page.position)
    for (const n of ns) sortRec(n.children)
  }
  sortRec(roots)
  return roots
}

/**
 * Flatten a tree depth-first with an indent level per node. Useful for the
 * "select a parent" dropdown in the CreatePageDialog.
 */
export function flattenTree(
  nodes: PageNode[],
  level = 0,
  out: Array<{ page: Page; level: number }> = [],
): Array<{ page: Page; level: number }> {
  for (const n of nodes) {
    out.push({ page: n.page, level })
    if (n.children.length) flattenTree(n.children, level + 1, out)
  }
  return out
}

/**
 * Walk from `id` up to the root, returning the chain (root first). Used to
 * build breadcrumb ancestors and to confirm cycle prevention on the client.
 */
export function findPath(pages: Page[], id: string): Page[] {
  const byId = new Map<string, Page>(pages.map((p) => [p.id, p]))
  const path: Page[] = []
  let cur: Page | undefined = byId.get(id)
  while (cur) {
    path.unshift(cur)
    cur = cur.parentPageId ? byId.get(cur.parentPageId) : undefined
  }
  return path
}

/**
 * Return true if `candidateId` appears anywhere in `pageId`'s ancestor chain.
 * Used to refuse re-parenting a node under one of its own descendants before
 * we even send the request.
 */
export function isDescendant(
  pages: Page[],
  candidateId: string,
  pageId: string,
): boolean {
  const path = findPath(pages, pageId)
  return path.some((p) => p.id === candidateId)
}

/**
 * Count every descendant of a page (the page itself excluded). Used to warn
 * before archival that the move cascades to N children.
 */
export function countDescendants(pages: Page[], pageId: string): number {
  const byParent = new Map<string | null, Page[]>()
  for (const p of pages) {
    const k = p.parentPageId
    const arr = byParent.get(k)
    if (arr) arr.push(p)
    else byParent.set(k, [p])
  }
  let n = 0
  const stack: string[] = [pageId]
  while (stack.length) {
    const cur = stack.pop()!
    const kids = byParent.get(cur) ?? []
    n += kids.length
    for (const k of kids) stack.push(k.id)
  }
  return n
}
